import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  signal
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AvailableLanguageCodesEnum, Language } from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';
import { UpperCasePipe } from '@angular/common';
import {
  ImmomioDropdown,
  ImmomioDropdownTrigger,
  ImmomioDropdownMenu,
  ImmomioDropdownGroup,
  ImmomioDropdownItem
} from '@immomio/ui/dropdown';
import { ImmomioButtonComponent } from '@immomio/ui/button';
import { LocaleFlagComponent } from '../../atoms/image/locale-flag/locale-flag.component';

@Component({
  selector: 'app-lang-picker',
  templateUrl: './lang-picker.component.html',
  styleUrls: ['./lang-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LangPickerComponent),
      multi: true
    }
  ],
  imports: [
    ImmomioDropdown,
    ImmomioDropdownTrigger,
    ImmomioDropdownMenu,
    ImmomioDropdownGroup,
    ImmomioDropdownItem,
    ImmomioButtonComponent,
    LocaleFlagComponent,
    UpperCasePipe,
    TranslatePipe
  ]
})
export class LangPickerComponent implements ControlValueAccessor {
  readonly availableLanguages = input<Language[]>(undefined);
  readonly defaultLanguageCode = input(AvailableLanguageCodesEnum.DE);
  readonly showFullLanguageNames = input(false);
  readonly ghostButton = input(false);
  readonly selectNextLanguageOnClick = input(false);

  protected readonly value = signal<AvailableLanguageCodesEnum>(undefined);

  private onChange = (value: unknown) => value;
  private onTouch = () => null;

  public onLanguageChange(langCode: AvailableLanguageCodesEnum) {
    if (this.value() === langCode) return;

    this.value.set(langCode);
    this.onChange(langCode);
    this.onTouch();
  }

  public onSelectNextLanguage(langCode: AvailableLanguageCodesEnum) {
    if (!this.selectNextLanguageOnClick()) return;

    const nextLangIndex =
      this.availableLanguages().findIndex(
        language => language.code === langCode
      ) + 1;
    const nextLangCode =
      this.availableLanguages()[
        nextLangIndex > this.availableLanguages().length - 1 ? 0 : nextLangIndex
      ].code;

    if (nextLangCode !== langCode) this.onLanguageChange(nextLangCode);
  }

  protected readonly selectedLanguage = computed(() => {
    const availableLanguages = this.availableLanguages();
    return (
      availableLanguages.find(lang => lang.code === this.value()) ||
      availableLanguages.find(
        lang => lang.code === this.defaultLanguageCode()
      ) ||
      availableLanguages[0]
    );
  });

  public writeValue(value: AvailableLanguageCodesEnum) {
    this.value.set(value);
  }

  public registerOnChange(fn) {
    this.onChange = fn;
  }

  public registerOnTouched(fn) {
    this.onTouch = fn;
  }
}
